import { useContext, useState, useMemo } from 'react';
import { Formik, Form, FieldArray } from 'formik';

import { makeStyles } from '@mui/styles';
import { FormControl, FormLabel } from '@mui/material';

import Button from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';
import Checkbox from '@classify/react-core-components/Checkbox';
import Callout from '@classify/react-core-components/Callout';
import Tag from '@classify/react-core-components/Tag';
import { RichContent } from '@openclassrooms/ui-web/rich-content';
import { FlashMessage } from '@openclassrooms/ui-web/notifications';
import { useSubmitExerciseSession } from '@openclassrooms/data-connect-web/assessment';
import browser from '@openclassrooms/utils/browser';
import { translate } from '@openclassrooms/translations';

import MultipleAnswerQuestion from './MultipleAnswerQuestion';
import SingleAnswerQuestion from './SingleAnswerQuestion';
import CourseExerciseContext from '../../contexts/courseExerciseContext';
import formatQuizSessionQuestions from '../../services/formatQuizSessionQuestions';
import CourseContext from '../../contexts/courseContext';

const useStyles = makeStyles(({ spacing, palette, typography, breakpoints }) => ({
  questionList: {
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(0, 2),
    [breakpoints.up('sm')]: {
      padding: spacing(0, 3),
    },
  },
  questionWrapper: {
    maxWidth: '100%',

    '&:is(:last-child)': {
      marginBottom: spacing(3),
    },

    '&:not(:last-child)': {
      marginBottom: spacing(8),
    },
  },
  legend: {
    width: '100%',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: spacing(2),
    paddingBottom: spacing(1),
    borderBottom: `1px solid ${palette.line}`,
  },

  content: {
    marginBottom: spacing(2),
    '& > p': {
      ...typography.subtitle1,
    },
    '& > pre, & > p': {
      margin: [0, '!important'],
    },
    '& > pre > code': {
      margin: [spacing(3, 0, 1), '!important'],
    },
  },
  explanation: {
    marginTop: spacing(1),
    '& > pre, & > p': {
      margin: [0, '!important'],
    },
  },
  submitInformationWrapper: {
    padding: spacing(4, 2),
    marginTop: spacing(1),
    borderTop: `1px solid ${palette.stroke}`,

    [breakpoints.up('sm')]: {
      padding: spacing(4, 3),
    },
  },
}));

interface Props {
  onSubmit: (hasSubmitted: boolean) => void;
}

const QuizQuestions = ({ onSubmit }: Props) => {
  const classes = useStyles();

  const [isUserConsentChecked, setIsUserConsentChecked] = useState(false);
  const [hasSubmitError, setSubmitError] = useState(false);
  const { exerciseId, exerciseSession, setExerciseSession, refetchQuizInformation } =
    useContext(CourseExerciseContext);
  const { refetchTocData } = useContext(CourseContext);
  const { questions, completedAt, exerciseSessionId, assessable } = exerciseSession || {};

  const [submitExerciseSession] = useSubmitExerciseSession();

  const remainingQuestionsTotalMemo = useMemo(() => {
    const questionsAnswered = questions.filter((question) => {
      const questionsSelected = question.answers.filter((answer) => answer.selected);

      return questionsSelected.length > 0;
    });

    return questions.length - questionsAnswered.length;
  }, [questions]);

  const [remainingQuestionsTotal, setRemainingQuestionsTotal] = useState(
    remainingQuestionsTotalMemo
  );

  const isCompleted = Boolean(completedAt);

  const initialValues: any = {};

  questions.forEach(({ id, answers }) => {
    initialValues[id] = answers
      .filter(({ selected }) => selected)
      .map(({ id: selectedAnswerId }) => selectedAnswerId);
  });

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors = {};

          const remainingQuestions = Object.values(values).filter(
            (answers) => answers.length === 0
          );

          if (remainingQuestions.length > 0) {
            errors.hasCompleted = false;

            setIsUserConsentChecked(false);
          }

          setRemainingQuestionsTotal(remainingQuestions.length);

          return errors;
        }}
        onSubmit={(values, actions) => {
          const completedQuestions = formatQuizSessionQuestions(questions, values);
          const exerciseSessionAnswers = {
            id: exerciseId,
            assessable,
            questions: completedQuestions,
          };

          submitExerciseSession(
            { exerciseSessionId, exerciseSessionAnswers },
            {
              onSettled() {
                actions.setSubmitting(false);
              },
              onSuccess(data) {
                setExerciseSession(data);
                refetchQuizInformation();
                refetchTocData();
                onSubmit(true);

                browser.window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              },
              onError() {
                setSubmitError(true);
              },
            }
          );
        }}
      >
        {(formik) => (
          <Form>
            <FieldArray
              name="quiz"
              render={() => (
                <div className={classes.questionList}>
                  {questions.map(
                    (
                      {
                        answers,
                        content,
                        id: questionId,
                        isMultiple,
                        explanation,
                        mcqQuestionSessionId,
                        correct,
                      },
                      index
                    ) => {
                      const questionName = translate('mcq.edit.message.question_title_num', {
                        num: index + 1,
                      });
                      const shouldDisplayExplanation = explanation && isCompleted;
                      const hasAnsweredQuestion = correct !== null;

                      return (
                        <FormControl
                          component="fieldset"
                          className={classes.questionWrapper}
                          key={`question-${questionId}`}
                        >
                          <FormLabel
                            component="legend"
                            className={classes.legend}
                            color="secondary"
                          >
                            <div className={classes.titleWrapper}>
                              <Typography variant="h4" component="p">
                                {questionName}
                              </Typography>
                              {hasAnsweredQuestion && (
                                <Tag
                                  color={correct ? 'success' : 'danger'}
                                  sx={{ position: 'absolute', right: 0 }}
                                >
                                  {correct
                                    ? translate('quiz.correction.question.answer_correct')
                                    : translate('quiz.correction.question.answer_incorrect')}
                                </Tag>
                              )}
                            </div>

                            <RichContent content={content} className={classes.content} />
                          </FormLabel>

                          {isMultiple ? (
                            <MultipleAnswerQuestion
                              answers={answers}
                              questionId={questionId}
                              isCompleted={isCompleted}
                              quizSessionQuestionId={mcqQuestionSessionId}
                            />
                          ) : (
                            <SingleAnswerQuestion
                              answers={answers}
                              questionId={questionId}
                              isCompleted={isCompleted}
                              quizSessionQuestionId={mcqQuestionSessionId}
                            />
                          )}

                          {shouldDisplayExplanation && (
                            <RichContent content={explanation} className={classes.explanation} />
                          )}
                        </FormControl>
                      );
                    }
                  )}
                </div>
              )}
            />

            {!isCompleted && (
              <div className={classes.submitInformationWrapper}>
                {remainingQuestionsTotal > 0 && (
                  <Callout type="info" className={classes.content}>
                    {translate(
                      'course.chapter.quiz.remaining_questions.content_block',
                      {
                        number: remainingQuestionsTotal,
                      },
                      remainingQuestionsTotal
                    )}
                  </Callout>
                )}
                {remainingQuestionsTotal === 0 && (
                  <Checkbox
                    className={classes.content}
                    label={translate('course.chapter.quiz.submit.checkbox')}
                    value={isUserConsentChecked}
                    defaultChecked={false}
                    onChange={(e) => setIsUserConsentChecked(e.target.checked)}
                  />
                )}
                <Button
                  disabled={!isUserConsentChecked || isCompleted}
                  variant="primary"
                  type="submit"
                  isLoading={formik.isSubmitting}
                >
                  {translate('global.word.validate')}
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
      <FlashMessage
        status="error"
        isOpen={hasSubmitError}
        onClose={() => setSubmitError(false)}
        autoCloseDuration={5000}
        translations={{ close: translate('global.action.close_button') }}
        canShowCloseButtonOnAutoClose
      >
        {translate('global.error.general_error')}
      </FlashMessage>
    </>
  );
};

export default QuizQuestions;
